import {FlattenT, useDataQuery} from '@tnc/shared';
import {useState} from 'react';

import {brandT} from '@/lib/types/brand.types';
import {marketT} from '@/lib/types/market.types';
import {positionInsertWithCustomItemT} from '@/lib/types/position.types';
import {productT} from '@/lib/types/product.types';

import {usdFormatter} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils/supabaseClass';

import {useNotifications} from '@/providers/components/notificationsProvider';

import {SearchAndSelectComponent} from '@/components';

/**
 * Form row component props
 * @interface FormRowComponentProps
 * @property {Function} onDelete - The function to call when the row is deleted
 */
type WholesalePositionRowPropsT = {
  onDelete: () => void;
  position: positionInsertWithCustomItemT;
  onUpdatePosition: (data: positionInsertWithCustomItemT) => void;
  onDuplicatePosition: (data: positionInsertWithCustomItemT) => void;
};

/**
 * Wholesale position row component
 * @param {FormRowComponentProps} onDelete - The function to call when the row is deleted
 * @returns {JSX.Element} - The form row component
 */
function WholesalePositionRow({onDelete, position, onUpdatePosition, onDuplicatePosition}: WholesalePositionRowPropsT) {
  const {toast} = useNotifications();

  // result states
  const [distributorIds, setDistributorIds] = useState<number[]>([]);

  // search states
  const [showMarketSearch, setShowMarketSearch] = useState<boolean>(false);
  const [showBrandSearch, setShowBrandSearch] = useState<boolean>(false);
  const [showProductSearch, setShowProductSearch] = useState<boolean>(false);

  // search terms
  const [marketSearchTerm, setMarketSearchTerm] = useState<string>('');
  const [brandSearchTerm, setBrandSearchTerm] = useState<string>('');
  const [productSearchTerm, setProductSearchTerm] = useState<string>('');

  const {data: {data: markets} = {data: []}} = useDataQuery({
    table: 'Market',
    columns: ['*'],
    searchTerm: marketSearchTerm,
    searchColumn: 'market_name',
    enabled: showMarketSearch,
  });

  const {data: {data: brands} = {data: []}} = useDataQuery({
    table: 'Distributor_Brand',
    columns: ['*', ['Brand', ['*']], ['Distributor', ['distributor_name']]],
    filters: [
      {
        column: 'fk_distributor_id',
        operator: 'in',
        value: distributorIds,
      },
      {
        column: 'date_removed',
        operator: 'is',
        value: null,
      },
    ],
    searchTerm: brandSearchTerm,
    searchColumn: 'Brand.brand_name',
    enabled: showBrandSearch,
  });

  const {data: {data: rateProducts} = {data: []}} = useDataQuery({
    table: 'Rates',
    columns: [
      '*',
      ['Product', ['product_id', 'product_name', 'product_image_url']],
      ['Brand', ['brand_id', 'brand_name', 'brand_logo_url']],
      ['Rate_Sheets', ['*', ['Distributor', ['distributor_name']]]],
    ],
    filters: [
      {
        column: 'fk_brand_id',
        operator: 'eq',
        value: position.item.brand?.brand_id,
      },
      {
        column: 'fk_market_id',
        operator: 'eq',
        value: position.item.market?.market_id,
      },
      {
        column: 'date_removed',
        operator: 'is',
        value: null,
      },
    ],
    searchTerm: productSearchTerm,
    searchColumn: 'Product.product_name',
    enabled: showProductSearch,
  });

  /**
   * Gets the distributor ids for a selected market to be used for brand search
   */
  const getDistributorIdsForMarket = async (marketId: number) => {
    if (!marketId) {
      return;
    }
    // Get all of the distributors for the market
    const {data, error} = await SupabaseInstance.supabase
      .from('Market_Distributor')
      .select('*')
      .eq('fk_market_id', marketId)
      .is('date_removed', null);

    if (error || !data) {
      console.error('Error fetching market distributors:', error);
      return;
    }

    // get all distributor ids from the market distributors
    const distributorIds = data.map(md => md.fk_distributor_id);

    setDistributorIds(distributorIds);
  };

  /**
   * Handles the market select event - updates the market state
   * @param {marketT} item - The market to select
   */
  const handleMarketSelect = async (item: marketT) => {
    await getDistributorIdsForMarket(item.market_id);
    const new_position = {
      ...position,
      item: {
        ...position.item,
        brand: null,
        fk_brand_id: null,
        product: null,
        fk_product_id: null,
        wholesale_price: 0,
        markup: 0,
        quantity: 0,
        error: false,
        errorMessage: '',
        market: item,
      },
    };

    onUpdatePosition(new_position);
    setShowMarketSearch(false);
  };

  /**
   * Handles the brand select event - updates the brand state
   * @param {brandT} item - The brand to select
   */
  const handleBrandSelect = (item: brandT) => {
    const new_position = {
      ...position,
      item: {
        ...position.item,
        brand: item,
        fk_brand_id: item.brand_id,
      },
    };

    onUpdatePosition(new_position);
    setShowBrandSearch(false);
  };

  /**
   * Handles the product select event - updates the product and position states
   * @param {productT} item - The product to select
   */
  const handleProductSelect = (
    item: FlattenT<
      productT & {
        tnc_price: number;
        min_quantity: number;
        factored_price: number;
        distributor_price: number;
        rate_id: number;
      }
    >
  ) => {
    if (!item.tnc_price) {
      toast.error('No wholesale price found for this product');
      return;
    }

    const new_position: positionInsertWithCustomItemT = {
      ...position,
      rate_id: item.rate_id,
      item: {
        ...position.item,
        quantity: item.min_quantity || 1,
        fk_product_id: item.product_id,
        product: item,
        markup: 0,
        wholesale_price: item.distributor_price,
        tnc_price: item.tnc_price,
        min_quantity: item.min_quantity,
        factored_price: item.factored_price,
        rate_id: item.rate_id,
      },
    };

    onUpdatePosition(new_position);
    setShowProductSearch(false);
  };

  /**
   * Handles the quantity change event - updates the quantity state
   * @param {number} newQuantity - The new quantity
   */
  const handleQuantityChange = (newQuantity: number) => {
    if (!position.item.product) return;

    const minQuantity = position.item.min_quantity || 1;
    // Round to nearest multiple of minQuantity
    const roundedQuantity = Math.max(minQuantity, Math.round(newQuantity / minQuantity) * minQuantity);

    const new_position = {
      ...position,
      item: {
        ...position.item,
        quantity: roundedQuantity,
      },
    };
    onUpdatePosition(new_position);
  };

  /**
   * Handles the markup change event - updates the markup state
   * @param {React.ChangeEvent<HTMLInputElement>} e - The event object
   */
  const handleMarkupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = parseFloat(e.target.value);
    const maxMarkupDollars = position.item.factored_price - position.item.tnc_price;

    const new_position = {
      ...position,
      item: {
        ...position.item,
        markup: Math.min(maxMarkupDollars, Math.max(0, inputValue)),
      },
    };
    onUpdatePosition(new_position);
  };

  return (
    <>
      {position.error && (
        <tr>
          <td colSpan={10}>
            <div className="alert alert-danger" role="alert">
              {position.errorMessage}
            </div>
          </td>
        </tr>
      )}
      <tr
        className={`border border-defaultborder dark:border-defaultborder/10 ${position.error ? 'outline outline-1 outline-red' : ''}`}
      >
        {/* MARKET */}
        <td>
          <div className="flex items-center">
            {position.item.market ? (
              <span className="mr-2">{position.item.market.market_name}</span>
            ) : (
              <span className="mr-2 text-gray-400">No market selected</span>
            )}
            <button
              aria-label="search"
              className="ti-btn ti-btn-sm ti-btn-light"
              onClick={() => {
                setShowMarketSearch(true);
              }}
            >
              <i className="ri-search-line"></i>
            </button>
          </div>

          <SearchAndSelectComponent
            title="Search and Select Market"
            isVisible={showMarketSearch}
            onClose={() => {
              setShowMarketSearch(false);
            }}
            onSelect={item => {
              handleMarketSelect(item);
            }}
            onSearch={(idx: string) => {
              setMarketSearchTerm(idx);
            }}
            results={markets || []}
            mapStructure={{
              displayFields: [
                {
                  title: 'Market Name',
                  field: 'market_name',
                },
              ],
              valueField: 'market_id',
            }}
          />
        </td>
        {/* BRAND */}
        <td>
          <div className="flex items-center">
            {position.item.brand ? (
              <span className="mr-2">{position.item.brand.brand_name}</span>
            ) : (
              <span className="mr-2 text-gray-400">No brand selected</span>
            )}
            <button
              aria-label="search"
              className="ti-btn ti-btn-sm ti-btn-light"
              onClick={() => {
                if (!position.item.market) {
                  alert('Please select a market first');
                } else {
                  setShowBrandSearch(true);
                }
              }}
            >
              <i className="ri-search-line"></i>
            </button>
          </div>

          <SearchAndSelectComponent
            title="Search and Select Brand"
            isVisible={showBrandSearch}
            onClose={() => {
              setShowBrandSearch(false);
            }}
            onSelect={(item: brandT) => {
              handleBrandSelect(item);
            }}
            onSearch={(idx: string) => {
              setBrandSearchTerm(idx);
            }}
            results={brands.map(brand => ({
              ...brand.Brand,
              Distributor: brand.Distributor?.distributor_name || 'No distributor',
            }))}
            mapStructure={{
              displayFields: [
                {
                  isImage: true,
                  title: 'Brand Logo',
                  field: 'brand_logo_url',
                },
                {
                  title: 'Brand Name',
                  field: 'brand_name',
                },
              ],
              valueField: 'brand_id',
            }}
          />
        </td>
        {/* PRODUCT */}
        <td>
          <div className="flex items-center">
            {position.item.product ? (
              <>
                {position.item.product.product_image_url && (
                  <img
                    src={position.item.product.product_image_url || '/images/logo/logo.png'}
                    alt="Product"
                    className="h-12 w-12 flex-shrink-0 rounded-md object-cover"
                  />
                )}
                <span className="mr-2 max-w-[15rem] truncate">{position.item.product.product_name}</span>
              </>
            ) : (
              <span className="mr-2 text-gray-400">No product selected</span>
            )}
            <button
              aria-label="search"
              className="ti-btn ti-btn-sm ti-btn-light"
              onClick={() => {
                if (!position.item.brand) {
                  alert('Please select a brand first');
                } else {
                  setShowProductSearch(true);
                }
              }}
            >
              <i className="ri-search-line"></i>
            </button>
          </div>

          <SearchAndSelectComponent
            title="Search and Select Product"
            isVisible={showProductSearch}
            onClose={() => {
              setShowProductSearch(false);
            }}
            onSelect={item => {
              handleProductSelect(item);
            }}
            onSearch={(idx: string) => {
              setProductSearchTerm(idx);
            }}
            results={rateProducts.map(rp => ({
              ...rp.Product,
              factored_price: rp.factored_price,
              distributor_price: rp.wholesale_price,
              tnc_price: rp.tnc_price,
              rate_id: rp.rate_id,
              min_quantity: rp.min_quantity,
              Distributor: rp.Rate_Sheets.Distributor.distributor_name,
            }))}
            mapStructure={{
              displayFields: [
                {
                  isImage: true,
                  title: 'Product Image',
                  field: 'product_image_url',
                },
                {
                  title: 'Name',
                  field: 'product_name',
                },
                {
                  title: 'Price',
                  field: `tnc_price`,
                  type: 'currency',
                },
                {
                  title: 'Minimum Order',
                  field: 'min_quantity',
                },
                {
                  title: 'Distributor',
                  field: 'Distributor',
                },
              ],
              valueField: 'product_id',
            }}
          />
        </td>
        {/* QUANTITY */}
        <td className="invoice-quantity-container relative">
          <div className="input-group !flex-nowrap rounded-md border dark:border-defaultborder/10">
            <button
              aria-label="decrease"
              type="button"
              className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-minus !mb-0 !rounded-e-none !bg-primary !text-white"
              onClick={() => handleQuantityChange(position.item.quantity - (position.item.min_quantity || 1))}
              disabled={!position.item.product}
            >
              <i className="ri-subtract-line"></i>
            </button>
            <input
              type="text"
              className="form-control !w-12 border-0 !p-1 text-center"
              value={position.item.quantity}
              onChange={e => handleQuantityChange(parseInt(e.target.value) || position.item.min_quantity || 1)}
              disabled={!position.item.product}
            />
            <button
              aria-label="increase"
              type="button"
              className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-plus !mb-0 !rounded-s-none !bg-primary !text-white"
              onClick={() => handleQuantityChange(position.item.quantity + (position.item.min_quantity || 1))}
              disabled={!position.item.product}
            >
              <i className="ri-add-line"></i>
            </button>
          </div>
          {position.item.min_quantity && (
            <div className="absolute -bottom-1 left-0 right-0 text-center">
              <span className="text-xs text-gray-500">Units: {position.item.min_quantity || '0'}</span>
            </div>
          )}
        </td>
        {/* WHOLESALE PRICE */}
        <td>
          <span className="form-control !rounded-md !p-0">{usdFormatter.format(position.item.wholesale_price)}</span>
        </td>
        {/* TNC PRICE */}
        <td>
          <span className="form-control !rounded-md !p-0">{usdFormatter.format(position.item.tnc_price)}</span>
        </td>
        {/* FACTORED PRICE */}
        <td>
          <span className="form-control !rounded-md !p-0">{usdFormatter.format(position.item.factored_price)}</span>
        </td>
        {/* MARGIN */}
        <td className="!pl-0">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="ml-2">$</span>
              <input
                className="form-control !w-20 !rounded-md !bg-light"
                type="number"
                min="0"
                max={`${(position.item.factored_price - position.item.tnc_price).toFixed(2)}`}
                step="0.1"
                value={position.item.markup}
                onChange={handleMarkupChange}
              />
            </div>
            {/* display the new price per unit */}
            <span className="ml-5 mt-1 text-xs text-gray-500">
              ${(position.item.tnc_price + position.item.markup).toFixed(2)} / unit
            </span>
          </div>
        </td>
        {/* MARKET VALUE */}
        <td>
          <span className="form-control !rounded-md !p-0">
            ${(position.item.tnc_price + position.item.markup) * position.item.quantity}
          </span>
        </td>
        {/* PROFIT */}
        <td>
          <span
            className="form-control !rounded-md !p-0"
            style={{
              color: 'green',
            }}
          >
            $
            {(position.item.tnc_price + position.item.markup) * position.item.quantity -
              position.item.tnc_price * position.item.quantity}
          </span>
        </td>
        {/* TOTAL COST */}
        <td>
          <span className="form-control !rounded-md !p-0">
            ${(position.item.quantity * position.item.tnc_price).toFixed(2)}
          </span>
        </td>
        {/* ACTION */}
        <td className="flex items-center gap-2">
          <button
            onClick={() => onDuplicatePosition(position)}
            aria-label="copy"
            type="button"
            className="ti-btn ti-btn-sm ti-btn-icon ti-btn-light"
          >
            <i className="ri-file-copy-line"></i>
          </button>

          <button
            aria-label="delete"
            type="button"
            className="ti-btn ti-btn-sm ti-btn-icon ti-btn-danger"
            onClick={onDelete}
          >
            <i className="ri-delete-bin-5-line"></i>
          </button>
        </td>
      </tr>
    </>
  );
}

export default WholesalePositionRow;
